import {
  DOCUMENT,
  inject,
  Injectable,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { Environment } from '@shared/models';

type MockFunc = (...args: unknown[]) => Promise<string>;

declare const grecaptcha: {
  enterprise: { ready: MockFunc; execute: MockFunc };
};

@Injectable({ providedIn: 'root' })
export class ReCaptchaService {
  private _token = '';
  private isLoaded = false;
  private readonly reCAPTCHA_SCRIPT =
    'https://www.google.com/recaptcha/enterprise.js?render=';

  private readonly document: Document = inject(DOCUMENT);
  private readonly rendererFactory: RendererFactory2 = inject(RendererFactory2);
  private renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);

  /**
   * Load reCAPTCHA script
   *
   * @param recaptchaKey reCAPTCHA key
   */
  loadScript(recaptchaKey: string): void {
    if (!this.isLoaded) {
      const script: HTMLScriptElement = this.renderer.createElement('script');
      script.type = 'text/javascript';
      script.src = `${this.reCAPTCHA_SCRIPT}${recaptchaKey}`;
      script.onload = (): void => {
        this.isLoaded = true;
      };
      this.renderer.appendChild(this.document.head, script);
    }
  }

  /**
   * Get reCAPTCHA Token
   *
   * @param recaptchaKey recaptcha key
   * @param env Environment Type
   * @param action handled action
   * @returns {Promise<string>} token
   */
  async getToken(
    recaptchaKey: string,
    env: Environment,
    action = 'LOGIN'
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!env.production) resolve('');

      if (!this.isLoaded) reject('reCAPTCHA script not loaded');

      const fn = async () => {
        this._token = await grecaptcha.enterprise.execute(recaptchaKey, {
          action,
        });
        resolve(this._token);
      };
      grecaptcha.enterprise.ready(fn);
    });
  }
}
